import { events } from "@constants/tracking";
import usePlayer from "@memorang/features/src/audio/usePlayer";
import AudioPlayer from "@memorang/ui/src/components/AudioPlayer";
import { useToast } from "@memorang/ui/src/components/Toast";
import { trackCustomEvent } from "analytics";
import { useLocalSearchParams } from "expo-router";
import { forwardRef, useCallback, useEffect, useImperativeHandle } from "react";
export type AudioPlayerRef = {
	play: () => Promise<void>;
	pause: () => void;
	isEnded: boolean;
	seekTo: (timeMs: number) => Promise<void>;
	isPlaying: boolean;
};

type Props = {
	uri: string;
	itemId?: number;
	onEnd?: () => void;
	onReplay?: () => void;
	hidden?: boolean;
	autoPlay?: boolean;
	waveformWidth?: number;
	updateWaveformWidth?: (width: number) => void;
	showWaveform?: boolean;
	useReanimated?: boolean;
	onDurationLoaded?: (durationInMilliseconds: number) => void;
};

const AudioPlayerContainer = forwardRef<AudioPlayerRef, Props>(
	(
		{
			uri,
			onEnd,
			onReplay,
			hidden,
			autoPlay,
			updateWaveformWidth,
			waveformWidth,
			showWaveform = true,
			useReanimated,
			onDurationLoaded,
			itemId,
		},
		ref,
	) => {
		const {
			isPlaying,
			currentTimeInMilliseconds,
			totalTimeInMilliseconds,
			pauseSound,
			playSound,
			isBuffering,
			onSeek,
			isEnded,
			isLoaded,
			hasError,
		} = usePlayer(uri, undefined, autoPlay);

		const { showToast } = useToast();

		const { id: sessionId } = useLocalSearchParams<{ id: string }>();

		const handleTracking = useCallback(
			(uri: string, type: "ended" | "started" | "paused") => {
				const getEventName = () => {
					switch (type) {
						case "ended":
							return events.sessionItemAudioEnded;
						case "started":
							return events.sessionItemAudioStarted;
						case "paused":
							return events.sessionItemAudioPaused;
						default:
							return "";
					}
				};

				trackCustomEvent({
					eventName: getEventName(),
					uri,
					type,
					itemId,
					sessionId,
				});
			},
			[itemId, sessionId],
		);

		useImperativeHandle(ref, () => ({
			play: playSound,
			pause: pauseSound,
			isEnded,
			seekTo: onSeek,
			isPlaying,
		}));

		const handlePlayPause = () => {
			if (isEnded) {
				onSeek(0);
				playSound();
				onReplay?.();
			} else if (isPlaying) {
				handleTracking?.(uri, "paused");
				pauseSound();
			} else {
				handleTracking?.(uri, "started");
				playSound();
			}
		};

		// biome-ignore lint/correctness/useExhaustiveDependencies: only called when isEnded changes
		useEffect(() => {
			if (isEnded) {
				handleTracking?.(uri, "ended");
				onEnd?.();
			}
		}, [isEnded]);

		useEffect(() => {
			if (!hasError) {
				return;
			}

			showToast({
				message: "An error occurred while playing the audio.",
			});
		}, [hasError, showToast]);

		// biome-ignore lint/correctness/useExhaustiveDependencies: only called when values change
		useEffect(() => {
			if (isLoaded && totalTimeInMilliseconds) {
				onDurationLoaded?.(totalTimeInMilliseconds);
			}
		}, [isLoaded, totalTimeInMilliseconds]);

		return (
			<AudioPlayer
				key={uri}
				uri={uri}
				currentTimeInMilliseconds={currentTimeInMilliseconds}
				totalTimeInMilliseconds={totalTimeInMilliseconds}
				isPlaying={isPlaying}
				handlePlayPause={handlePlayPause}
				isBuffering={isBuffering || !isLoaded}
				isEnded={isEnded}
				onSeek={onSeek}
				hidden={hidden}
				waveformWidth={waveformWidth}
				updateWaveformWidth={updateWaveformWidth}
				showWaveform={showWaveform}
				useReanimated={useReanimated}
			/>
		);
	},
);

export default AudioPlayerContainer;
